import { Team } from '@ddt/protocol';
import { minimapLayout, minimapToWorld, rectToMinimap, rulerMarks, worldToMinimap, type MinimapLayout, type Point, type Rect } from './camera';
import type { GameState } from './state';

export const TEAM_COLORS: Record<number, number> = { [Team.A]: 0x4cc3ff, [Team.B]: 0xff7a59 };

/** Espaço mínimo entre marcas da régua, em px do minimapa, para o número caber. */
const RULER_LABEL_MIN_GAP = 8;

/** Altura máxima do minimapa em relação à largura disponível: mapas altos ficam mais estreitos, não mais altos. */
export const MINIMAP_MAX_HEIGHT_RATIO = 0.6;

/** O que o minimapa lê e comanda. O `GameRenderer` implementa; os testes usam um falso. */
export interface MinimapSource {
  readonly state: GameState;
  /** Muda quando o terreno desenhado muda (mapa novo, cratera): o minimapa refaz a miniatura só nesse caso. */
  readonly terrainVersion: number;
  terrainImage(): CanvasImageSource | null;
  projectilePoint(): Point | null;
  viewRect(): Rect;
  panTo(x: number, y: number): void;
  beginCameraDrag(): void;
  endCameraDrag(): void;
}

interface Drag {
  pointerId: number;
  /** Distância do ponto agarrado ao centro do indicador, em coordenadas do mapa: o indicador não salta para o cursor. */
  grabX: number;
  grabY: number;
}

/**
 * Minimapa da partida num canvas 2D: o mapa inteiro com a proporção real, o terreno reduzido, os personagens, o projétil e o
 * indicador da região que a câmera mostra. Arrastar o indicador move a câmera; clicar fora dele centra a câmera no ponto e
 * continua o arraste. Só muda a câmera: nenhuma ação de combate sai daqui.
 */
export class Minimap {
  /** Começa inválido (-1) para o primeiro `draw` sempre fixar o tamanho do canvas, também sem mapa (0 px, não os 300x150 padrão). */
  private layout: MinimapLayout = { scale: 0, width: -1, height: -1 };
  private boxWidth = 0;
  private dpr = 1;
  private thumbnail: HTMLCanvasElement | null = null;
  private thumbnailKey = '';
  private drawnKey = '';
  private drag: Drag | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private destroyed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly source: MinimapSource,
  ) {
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerEnd);
    canvas.addEventListener('pointercancel', this.onPointerEnd);
    canvas.addEventListener('lostpointercapture', this.onPointerEnd);
    window.addEventListener('blur', this.onBlur);
    const box = canvas.parentElement;
    if (box && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(box);
    }
    this.measure();
  }

  get dragging(): boolean {
    return this.drag !== null;
  }

  /** Tamanho atual do minimapa em pixels CSS (0 sem mapa). */
  get size(): { width: number; height: number } {
    return { width: this.layout.width, height: this.layout.height };
  }

  /** Um quadro: refaz o layout se o mapa ou o espaço mudou e redesenha só se algo visível mudou. */
  draw(): void {
    if (this.destroyed) return;
    this.updateLayout();
    const key = this.frameKey();
    if (key === this.drawnKey) return;
    this.drawnKey = key;
    this.paint();
  }

  /** Encerra o arraste em andamento (mapa novo, partida encerrada, janela sem foco). */
  cancelDrag(): void {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    delete this.canvas.dataset.dragging;
    if (this.canvas.hasPointerCapture?.(drag.pointerId)) this.canvas.releasePointerCapture(drag.pointerId);
    this.source.endCameraDrag();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.cancelDrag();
    this.destroyed = true;
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerEnd);
    this.canvas.removeEventListener('pointercancel', this.onPointerEnd);
    this.canvas.removeEventListener('lostpointercapture', this.onPointerEnd);
    window.removeEventListener('blur', this.onBlur);
    this.resizeObserver?.disconnect();
    this.thumbnail = null;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || this.layout.scale <= 0) return;
    event.preventDefault();
    this.cancelDrag();
    const point = this.toWorld(event);
    const rect = this.source.viewRect();
    const inside = point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;
    this.drag = {
      pointerId: event.pointerId,
      grabX: inside ? point.x - (rect.x + rect.width / 2) : 0,
      grabY: inside ? point.y - (rect.y + rect.height / 2) : 0,
    };
    this.canvas.dataset.dragging = 'true';
    // Com a captura, o arraste continua quando o cursor sai do minimapa e o soltar sempre chega aqui.
    this.canvas.setPointerCapture?.(event.pointerId);
    this.source.beginCameraDrag();
    this.source.panTo(point.x - this.drag.grabX, point.y - this.drag.grabY);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    // O botão foi solto sem o pointerup chegar (por exemplo, fora da janela): encerra em vez de seguir o cursor.
    if (event.buttons === 0 && event.pointerType === 'mouse') {
      this.cancelDrag();
      return;
    }
    const point = this.toWorld(event);
    this.source.panTo(point.x - drag.grabX, point.y - drag.grabY);
  };

  private readonly onPointerEnd = (event: PointerEvent): void => {
    if (this.drag && event.pointerId === this.drag.pointerId) this.cancelDrag();
  };

  private readonly onBlur = (): void => this.cancelDrag();

  private toWorld(event: PointerEvent): Point {
    const bounds = this.canvas.getBoundingClientRect();
    return minimapToWorld({ x: event.clientX - bounds.left, y: event.clientY - bounds.top }, this.layout);
  }

  /** Largura útil do elemento pai (sem o padding): o minimapa se ajusta ao espaço que a interface reserva. */
  private measure(): void {
    const box = this.canvas.parentElement;
    if (!box) {
      this.boxWidth = 0;
    } else {
      const style = getComputedStyle(box);
      this.boxWidth = Math.max(0, box.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0));
    }
    this.drawnKey = '';
  }

  private updateLayout(): void {
    const state = this.source.state;
    const map = { width: state.mapWidth, height: state.mapHeight };
    const box = { width: this.boxWidth, height: this.boxWidth * MINIMAP_MAX_HEIGHT_RATIO };
    const layout = minimapLayout(map, box);
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    if (layout.width === this.layout.width && layout.height === this.layout.height && dpr === this.dpr) return;
    this.layout = layout;
    this.dpr = dpr;
    this.canvas.style.width = `${layout.width}px`;
    this.canvas.style.height = `${layout.height}px`;
    this.canvas.width = Math.max(1, Math.round(layout.width * dpr));
    this.canvas.height = Math.max(1, Math.round(layout.height * dpr));
    this.thumbnailKey = '';
    this.drawnKey = '';
  }

  /** Tudo que muda o desenho, arredondado ao pixel do mapa: a posição suavizada parada não pede redesenho. */
  private frameKey(): string {
    const state = this.source.state;
    const rect = this.source.viewRect();
    const parts = [
      this.layout.width,
      this.layout.height,
      this.dpr,
      this.source.terrainVersion,
      Math.round(rect.x),
      Math.round(rect.y),
      Math.round(rect.width),
      Math.round(rect.height),
      state.turn?.accountId ?? '',
    ];
    for (const c of state.combatants.values()) parts.push(c.accountId, Math.round(c.displayX), Math.round(c.displayY), c.alive ? 1 : 0, c.team);
    const shot = this.source.projectilePoint();
    if (shot) parts.push('p', Math.round(shot.x), Math.round(shot.y));
    return parts.join('|');
  }

  private refreshThumbnail(): void {
    const key = `${this.source.terrainVersion}|${this.canvas.width}|${this.canvas.height}`;
    if (key === this.thumbnailKey) return;
    this.thumbnailKey = key;
    const image = this.source.terrainImage();
    if (!image) {
      this.thumbnail = null;
      return;
    }
    const thumb = this.thumbnail ?? document.createElement('canvas');
    thumb.width = this.canvas.width;
    thumb.height = this.canvas.height;
    const ctx = thumb.getContext('2d');
    if (!ctx) {
      this.thumbnail = null;
      return;
    }
    ctx.clearRect(0, 0, thumb.width, thumb.height);
    ctx.drawImage(image, 0, 0, thumb.width, thumb.height);
    this.thumbnail = thumb;
  }

  private paint(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx || this.layout.scale <= 0) return;
    this.refreshThumbnail();
    const { width, height } = this.layout;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.thumbnail) ctx.drawImage(this.thumbnail, 0, 0);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    const state = this.source.state;
    for (const c of state.combatants.values()) {
      const p = worldToMinimap({ x: c.displayX, y: c.displayY - 10 }, this.layout);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = c.alive ? css(TEAM_COLORS[c.team] ?? 0xffffff) : '#6b7280';
      ctx.fill();
      if (c.alive && state.turn?.accountId === c.accountId && state.status === 'playing') {
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#ffb020';
        ctx.stroke();
      }
    }

    const shot = this.source.projectilePoint();
    if (shot) {
      const p = worldToMinimap(shot, this.layout);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffd27a';
      ctx.fill();
    }

    // Indicador da câmera: a linha fica toda dentro do minimapa, também quando a câmera mostra o mapa inteiro.
    const r = rectToMinimap(this.source.viewRect(), this.layout);
    const x = Math.max(0, Math.min(r.x, width));
    const y = Math.max(0, Math.min(r.y, height));
    const w = Math.max(1, Math.min(r.width, width - x));
    const h = Math.max(1, Math.min(r.height, height - y));
    ctx.fillStyle = 'rgb(255 255 255 / 12%)';
    ctx.fillRect(x, y, w, h);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgb(255 255 255 / 90%)';
    ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, w - 1), Math.max(0, h - 1));
    this.paintRuler(ctx, y, h);
  }

  /** Régua dentro do indicador: o jogador mede a distância do tiro em décimos da tela, como no legado. */
  private paintRuler(ctx: CanvasRenderingContext2D, top: number, height: number): void {
    const marks = rulerMarks(this.source.viewRect()).map((m) => ({ unit: m.unit, x: Math.round(m.x * this.layout.scale) + 0.5 }));
    if (marks.length === 0) return;
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgb(255 255 255 / 70%)';
    ctx.beginPath();
    for (const m of marks) {
      ctx.moveTo(m.x, top);
      ctx.lineTo(m.x, top + height);
    }
    ctx.stroke();
    const gap = marks.length > 1 ? marks[1]!.x - marks[0]!.x : Infinity;
    if (gap < RULER_LABEL_MIN_GAP) return;
    ctx.font = '600 8px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    for (const m of marks) ctx.fillText(String(m.unit), m.x, top + height / 2);
  }
}

function css(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}
